import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import Footer from "@/components/Footer";
import Navigation from "@/components/Navigation";
import MyTickets from "@/components/tickets/MyTickets";
import { TICKET_PAGE_CSS } from "@/components/tickets/TicketPurchaseParts";
import { useOptionalAuth } from "@/contexts/AuthContext";
import { useBuyerEmailRecognition } from "@/hooks/useBuyerEmailRecognition";
import { trpc } from "@/lib/trpc";
import { inputStyle } from "@/components/memberForm/fields";
import { normalizeGuestEmail, validGuestEmail } from "@/lib/guestTicketProfile";
import { readTicketJoinInterest, type MyTicket } from "@/lib/ticketingApi";

type GuestSession = { email: string; name: string; companyName: string; jobTitle: string; phone: string } | null;

export default function GuestTickets() {
  const [, navigate] = useLocation();
  const auth = useOptionalAuth();
  const events: any = trpc.events;
  const sessionQuery = events.ticketGuestSession.useQuery(undefined, { retry: false, refetchOnWindowFocus: false });
  const start = events.ticketGuestStart.useMutation();
  const verify = events.ticketGuestVerify.useMutation();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const session = sessionQuery.data as GuestSession | undefined;
  const emailRecognition = useBuyerEmailRecognition(session?.email ?? "", !auth?.isLoggedIn);

  useEffect(() => {
    if (session?.email) setEmail(session.email);
  }, [session?.email]);

  async function requestCode() {
    const normalized = normalizeGuestEmail(email);
    if (!validGuestEmail(normalized)) {
      setError("Enter a valid email address.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      // Access-only requests intentionally carry email alone: no guest profile can be changed here.
      await start.mutateAsync({ email: normalized, accessOnly: true });
      setSent(true);
      setCode("");
    } catch {
      setError("We couldn't send a code for that email. Check the address and try again.");
    } finally {
      setBusy(false);
    }
  }

  async function verifyCode(value: string) {
    setCode(value);
    if (value.length !== 6 || !sent || busy) return;
    setBusy(true);
    setError("");
    try {
      await verify.mutateAsync({ email: normalizeGuestEmail(email), code: value, accessOnly: true });
      await sessionQuery.refetch();
    } catch {
      setError("That code couldn't be verified. Try again or request a new code.");
    } finally {
      setBusy(false);
    }
  }

  const hasGuestSession = Boolean(session?.email);
  return (
    <div className="chi-ticket-page" style={{ background: "#080C18", minHeight: "100vh" }}>
      <style>{TICKET_PAGE_CSS}</style>
      <Navigation />
      <main style={{ maxWidth: 1120, margin: "0 auto", padding: "100px 16px 64px", fontFamily: "'Space Grotesk', sans-serif", color: "#CBD5E1" }}>
        <header style={{ marginBottom: "2rem" }}>
          <button type="button" onClick={() => navigate("/calendar-of-events")} className="tp-link" style={{ background: "none", border: 0, padding: 0 }}>Back to events</button>
          <h1 style={{ fontSize: "clamp(1.8rem,4vw,2.5rem)", color: "white", margin: ".75rem 0" }}>Your tickets</h1>
          <p>Open the ticket link from your order email, or request a one-time code to view your tickets.</p>
        </header>
        {sessionQuery.isLoading ? (
          <div role="status" aria-live="polite" style={{ maxWidth: 500 }}>
            <div style={{ height: 18, width: "55%", background: "rgba(255,255,255,.08)", borderRadius: 6 }} />
            <div style={{ height: 110, marginTop: 16, background: "rgba(255,255,255,.05)", borderRadius: 12 }} />
          </div>
        ) : hasGuestSession ? (
          <MyTickets
            allowGuestEdits={false}
            readyContent={(issuedTickets: MyTicket[]) => {
              if (!session?.email || auth?.isLoggedIn || emailRecognition.status !== "guest") return null;
              const optedInTicket = issuedTickets.find(ticket => (
                ticket.status === "active" && Boolean(ticket.qrToken) && readTicketJoinInterest(ticket.eventSlug)
              ));
              if (!optedInTicket) return null;
              return (
                <section
                  data-testid="guest-checkout-join-invitation"
                  aria-labelledby="guest-checkout-join-title"
                  style={{ marginTop: 20, padding: "18px 20px", border: "1px solid rgba(201,160,96,.55)", borderRadius: 14, background: "linear-gradient(145deg,#251a23,#302326)", color: "#f7eee0" }}
                >
                  <p style={{ margin: "0 0 5px", color: "#d9bd83", fontSize: ".68rem", fontWeight: 750, letterSpacing: ".13em", textTransform: "uppercase" }}>Your tickets are ready</p>
                  <h2 id="guest-checkout-join-title" style={{ margin: "0 0 8px", color: "#fff5e8", fontFamily: "Georgia,serif", fontSize: "1.35rem" }}>Stay connected beyond this event</h2>
                  <p style={{ margin: "0 0 15px", color: "#d2c7bd", fontSize: ".87rem", lineHeight: 1.55 }}>
                    Join the CHI Alliance for free to connect with fellow professionals, discover upcoming events, and stay involved.
                  </p>
                  <Link
                    href="/create-account?source=ticket-checkout&returnTo=%2Fguest-tickets"
                    className="chi-btn-primary"
                    data-testid="guest-checkout-create-account"
                    style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", minHeight: 46, padding: "10px 16px", textDecoration: "none" }}
                  >
                    Create my free account
                  </Link>
                </section>
              );
            }}
          />
        ) : (
          <section className="chi-ticket-card" aria-labelledby="guest-ticket-access-heading" style={{ maxWidth: 520 }}>
            <h2 id="guest-ticket-access-heading" style={{ color: "white", fontSize: "1.2rem" }}>Access guest tickets</h2>
            <p>We'll verify your email without creating or signing in to a member account.</p>
            <label className="tp-label" htmlFor="guest-ticket-email">Email address</label>
            <input id="guest-ticket-email" type="email" inputMode="email" autoComplete="email" required value={email} onChange={event => { setEmail(event.target.value); setSent(false); setCode(""); setError(""); }} style={inputStyle} />
            {!sent ? (
              <button type="button" className="chi-btn-primary" onClick={() => void requestCode()} disabled={busy || !validGuestEmail(email)} style={{ marginTop: 16 }}>
                {busy ? "Sending code…" : "Send access code"}
              </button>
            ) : (
              <div style={{ marginTop: 20 }}>
                <p>Enter the 6-digit code sent to {normalizeGuestEmail(email)}.</p>
                <InputOTP maxLength={6} value={code} onChange={verifyCode} autoComplete="one-time-code" inputMode="numeric" pattern="[0-9]*" aria-label="6-digit email access code">
                  <InputOTPGroup>{[0, 1, 2, 3, 4, 5].map(index => <InputOTPSlot key={index} index={index} style={{ width: 48, height: 48 }} />)}</InputOTPGroup>
                </InputOTP>
                {busy && <p role="status">Verifying access code…</p>}
                <button type="button" className="tp-link" onClick={() => { setSent(false); setCode(""); }} style={{ border: 0, background: "none", padding: 0, marginTop: 12 }}>Use a different email or request another code</button>
              </div>
            )}
            {error && <p role="alert" className="tp-error">{error}</p>}
            {sessionQuery.isError && <p role="alert" className="tp-error">We couldn't check for a guest session. <button type="button" className="tp-link" onClick={() => void sessionQuery.refetch()}>Try again</button></p>}
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
